/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useContext, useEffect } from 'react';
import './manage-stakeholder-group.scss';
import G17Client from '../../../services/G17Client';
import { TeamMemberPopup } from './TeamMemberPopup';
import { AssuranceRoles } from '../../../constants/assurance';
import { ACTION, TeamMemberContext } from './TeamMemberContainer';
import NotAuthorised from '../../../routes/not-authorised';
import { OrganizationAssurancePortfolio } from '../../../types/assurance';
import { BlockingLoader } from '@g17eco/atoms/loader';

interface Props {
  assurancePortfolio: OrganizationAssurancePortfolio;
  canManage?: boolean;
  canAssure?: boolean;
}

export const ManageStakeholderGroup = ({
  assurancePortfolio,
  canManage = false,
  canAssure = false,
}: Props) => {
  const { portfolioData, submitData, dispatch } = useContext(TeamMemberContext);

  useEffect(() => {
    if (!portfolioData.loading && !portfolioData.loaded && !portfolioData.errored) {
      dispatch({
        type: ACTION.SET_PORTFOLIO_DATA,
        payload: {
          loading: true,
        },
      });
      G17Client.getAssurancePermissions(assurancePortfolio._id)
        .then((data) => {
          return dispatch({
            type: ACTION.SET_PORTFOLIO_DATA,
            payload: {
              data: {
                assurers: {
                  [AssuranceRoles.Admin]: data.assurers.filter((assurer) => assurer.isAdmin),
                  [AssuranceRoles.Assurer]: data.assurers.filter((assurer) => assurer.isAssurer),
                },
                organizationId: data.organizationId,
                portfolioId: assurancePortfolio._id,
              },
              loaded: true,
              loading: false,
            },
          });
        })
        .catch((e) => {
          dispatch({
            type: ACTION.SET_PORTFOLIO_DATA,
            payload: {
              loading: false,
              errored: true,
              errorMessage: e.message,
            },
          });
        });
    }
  }, [portfolioData, assurancePortfolio._id, dispatch]);

  if (!canAssure) {
    return <NotAuthorised />;
  }

  const isLastAdmin = portfolioData.data.assurers[AssuranceRoles.Admin].length <= 1;

  return (
    <div className={'add-stakeholder'}>
      {!portfolioData.loaded || submitData.submitting ? <BlockingLoader /> : null}
      <div>
        <div className='ml-2'>
          <p>All the Assurers currently assigned access to the Assurance Tracker</p>
        </div>
        <div className='mt-3'>
          <div className='pb-2 pb-lg-0 pr-lg-0 d-flex user-wrapper'>
            {[AssuranceRoles.Admin, AssuranceRoles.Assurer].map((role) => (
              <TeamMemberPopup canManage={canManage} isLastAdmin={isLastAdmin} key={role} role={role} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
